import { claimsPer1000, paretoOf, recordDelayCost, recordNpr, rpnAfter, rpnBefore } from "./calc";
import type { ProblemRecord, SeriesPoint, WarrantyDataSet } from "./types";

export const recordsForProgram = (data: WarrantyDataSet, programId: string | null | undefined) =>
  programId ? data.records.filter((r) => r.programId === programId) : data.records;

export function portfolio(records: ProblemRecord[]) {
  const open = records.filter((r) => r.status !== "closed");
  const exposure = records.reduce((a, r) => a + r.financial.exposure, 0);
  const actual = records.reduce((a, r) => a + r.financial.actualCost, 0);
  const npr = records.reduce((a, r) => a + recordNpr(r), 0);
  const delay = records.reduce((a, r) => a + recordDelayCost(r), 0);
  const recovered = records.reduce((a, r) => a + r.financial.supplierRecovered, 0);
  const recoveryTarget = records.reduce((a, r) => a + r.financial.supplierRecoveryTarget, 0);
  const claims = records.reduce((a, r) => a + r.claims, 0);
  const units = records.reduce((a, r) => a + r.unitsInService, 0);
  const critical = records.filter((r) => r.risk === "critical").length;
  const gated = records.filter((r) =>
    r.stages.some((s) => s.status !== "complete" && s.evidence.length < s.requiredEvidence.length),
  ).length;
  return {
    total: records.length,
    open: open.length,
    closed: records.length - open.length,
    exposure,
    actual,
    npr,
    delay,
    recovered,
    recoveryTarget,
    claims,
    units,
    critical,
    gated,
    c1000: claimsPer1000(claims, Math.max(units, 1)),
  };
}

export function blendedSeries(records: ProblemRecord[], key: "c1000" | "imr"): SeriesPoint[] {
  const first = records[0];
  if (!first) return [];
  return first[key].map((p, i) => ({
    month: p.month,
    value:
      Math.round(
        (records.reduce((a, r) => a + (r[key][i]?.value ?? 0), 0) / records.length) * 100,
      ) / 100,
  }));
}

export function blendedForecast(records: ProblemRecord[]) {
  const first = records[0];
  if (!first) return [];
  return first.financial.forecast.map((p, i) => ({
    month: p.month,
    actual: records.some((r) => r.financial.forecast[i]?.actual == null)
      ? records.reduce((a, r) => a + (r.financial.forecast[i]?.actual ?? 0), 0) || null
      : records.reduce((a, r) => a + (r.financial.forecast[i]?.actual ?? 0), 0),
    aiForecast: records.reduce((a, r) => a + (r.financial.forecast[i]?.aiForecast ?? 0), 0),
  }));
}

export const topFive = (records: ProblemRecord[]) =>
  [...records].sort((a, b) => b.riskScore - a.riskScore).slice(0, 5);

export const causePareto = (records: ProblemRecord[]) =>
  paretoOf(records.map((r) => ({ label: r.failureCause, value: r.claims })));

export const rpnDelta = (r: ProblemRecord) => {
  const before = r.pfmea.reduce((a, row) => Math.max(a, rpnBefore(row)), 0);
  const after = r.pfmea.reduce((a, row) => Math.max(a, rpnAfter(row)), 0);
  return {
    before,
    after,
    reduction: before === 0 ? 0 : Math.round(((before - after) / before) * 100),
  };
};

export const CLOSED_LOOP_STAGES = [
  {
    id: 1,
    name: "Connected Signal",
    detail:
      "Warranty claims, telematics and dealer comments are fused into one aggregate signal with statistical confirmation.",
  },
  {
    id: 2,
    name: "Management Top 5",
    detail:
      "Signals are ranked by risk and financial exposure and accepted into governance review.",
  },
  {
    id: 3,
    name: "Governed Problem Record",
    detail:
      "One record holds 8D, PFMEA and control plan as chapters, with owner, champion and clause mapping.",
  },
  {
    id: 4,
    name: "Containment",
    detail:
      "Customer protection at plant, in-transit, dealer stock and supplier with verified effect.",
  },
  {
    id: 5,
    name: "Root Cause & Corrective Action",
    detail: "Occurrence, escape and systemic causes proven with data before actions are selected.",
  },
  {
    id: 6,
    name: "Change & Breakpoint",
    detail:
      "Engineering or process change authorised, validated and confirmed at a traceable breakpoint.",
  },
  {
    id: 7,
    name: "PFMEA / Control Plan Update",
    detail:
      "Ratings and controls are revised with rationale wherever a rating is deliberately held.",
  },
  {
    id: 8,
    name: "Effectiveness & Prevention",
    detail:
      "Post-breakpoint claims measured against target reduction; prevention institutionalised.",
  },
  {
    id: 9,
    name: "Read-Across & Closure",
    detail: "Similar platforms screened, actions assigned, closure signed off by governance.",
  },
] as const;
